import { size } from 'lodash'
import {
  DynamicPageError,
  InvalidCharsInPrefixError,
} from './dynamicPageErrors'
import { DPErrorCodes, DYNAMIC_PAGES_PER_COLLECTION_LIMIT } from './constants'
import type { AppApi, PageType } from '@wix/wix-data-client-common'
import {
  containsNonLatinChars,
  getExistingPatternsByPrefix,
  PAGE_TYPES,
  patternHasFields,
  validatePrefixWithLatinOnly,
} from '@wix/wix-data-client-common'
import type { EditorSDK } from '@wix/wix-data-client-common-standalone'

const MAX_URL_LENGTH = 40

const SAFE_PADDING_FOR_URI_LENGTH = 5

const isPageUriSeoTooLong = (pageUriSEO: string, marginForError = 0) =>
  size(pageUriSEO) > MAX_URL_LENGTH - marginForError

const ensureValidUriSEOLength = (pageUriSEO: string) =>
  pageUriSEO.slice(0, MAX_URL_LENGTH - SAFE_PADDING_FOR_URI_LENGTH)

const safelyGetExistingPatternsByPrefix = async (
  editorSdkProxy: EditorSDK,
  prefix: string,
) => {
  try {
    return await getExistingPatternsByPrefix(editorSdkProxy, prefix)
  } catch {
    return []
  }
}

export const isDuplicateRouterPrefix = async ({
  editorSdkProxy,
  prefix,
  ignorePageType,
  pageType = PAGE_TYPES.ITEM,
}: {
  editorSdkProxy: EditorSDK
  prefix: string
  ignorePageType: boolean
  pageType?: PageType
}) => {
  const existingPatterns = await safelyGetExistingPatternsByPrefix(
    editorSdkProxy,
    prefix,
  )

  if (ignorePageType) {
    return existingPatterns.length > 0
  }

  const existingPatternsMatchingPageType = existingPatterns.filter(pattern =>
    pageType === PAGE_TYPES.ITEM
      ? patternHasFields(pattern)
      : !patternHasFields(pattern),
  )

  return existingPatternsMatchingPageType.length > 0
}

const isPrefixValid = async ({
  editorSdkProxy,
  prefix,
  pageType,
  ignorePageType,
}: {
  editorSdkProxy: EditorSDK
  prefix: string
  pageType?: PageType
  ignorePageType: boolean
}) => {
  const { valid, message, errorCode } = await validatePrefixWithLatinOnly({
    editorSdkProxy,
    prefix,
  })

  if (!valid) {
    const prefixErrorCodes = editorSdkProxy.routers.InvalidPrefixReason

    const errorCodesForRetry = [
      prefixErrorCodes.PREFIX_IS_DUPLICATE_OF_URI_SEO,
      prefixErrorCodes.PREFIX_IS_FORBIDDEN_WORD,
      prefixErrorCodes.PREFIX_IS_IN_USE_BY_ANOTHER_APPLICATION,
    ]

    if (errorCodesForRetry.includes(errorCode)) {
      return false
    }

    if (
      errorCode ===
      editorSdkProxy.routers.InvalidPrefixReason
        .PREFIX_CONTAINS_INVALID_CHARACTERS
    ) {
      throw new InvalidCharsInPrefixError(message)
    }

    throw new Error(`Prefix "${prefix}" is not valid: ${message} ${errorCode}`)
  }

  const isDuplicate = await isDuplicateRouterPrefix({
    editorSdkProxy,
    prefix,
    pageType,
    ignorePageType,
  })
  return !isDuplicate
}

const generatePrefix = async ({
  title,
  editorSdkProxy,
  appApi,
  pageType,
  ignorePageType,
}: {
  title: string
  editorSdkProxy: EditorSDK
  appApi: AppApi
  pageType?: PageType
  ignorePageType: boolean
}) => {
  let pageUriSEO =
    await editorSdkProxy.document.pages.pageUriSEO.convertPageNameToUrl({
      title,
    })

  if (containsNonLatinChars(pageUriSEO)) {
    pageUriSEO = await appApi.transliterateAndSlugify(pageUriSEO)
  }

  if (isPageUriSeoTooLong(pageUriSEO, SAFE_PADDING_FOR_URI_LENGTH)) {
    pageUriSEO = ensureValidUriSEOLength(pageUriSEO)
  }

  let prefixIsValid = false
  let increment = 0
  let prefix

  do {
    if (increment <= -DYNAMIC_PAGES_PER_COLLECTION_LIMIT) {
      throw new DynamicPageError(
        DPErrorCodes.TOO_MANY_DYNAMIC_PAGES_FOR_COLLECTION,
        `${DPErrorCodes.TOO_MANY_DYNAMIC_PAGES_FOR_COLLECTION}: Failed to generate a unique prefix for title "${title}"`,
      )
    }

    prefix = `${pageUriSEO}${increment || ''}`
    prefixIsValid = await isPrefixValid({
      editorSdkProxy,
      prefix,
      pageType,
      ignorePageType,
    })

    increment--
  } while (!prefixIsValid)

  return prefix
}

export const generateUniquePrefix = async ({
  inputsForPrefix,
  editorSdkProxy,
  appApi,
  pageType,
  ignorePageType = false,
}: {
  inputsForPrefix: string[]
  editorSdkProxy: EditorSDK
  appApi: AppApi
  pageType?: PageType
  ignorePageType?: boolean
}) => {
  for (const [index, input] of inputsForPrefix.entries()) {
    try {
      const uniquePrefix = await generatePrefix({
        title: input,
        editorSdkProxy,
        appApi,
        pageType,
        ignorePageType,
      })
      return uniquePrefix
    } catch (error) {
      const isLastInput = index === inputsForPrefix.length - 1
      if (!isLastInput && error instanceof InvalidCharsInPrefixError) {
        continue
      }
      throw new Error('Failed to generate unique prefix', { cause: error })
    }
  }
  return ''
}
